<template>
  <div
    class="auth-form"
    :class="{
      loading: isFormInProgress,
    }"
  >
    <!--begin::Form-->
    <template v-if="passwordResetSent">
      <AccountPasswordResetSent
        :email="resetEmail"
        @back="handleBackFromPasswordReset"
      />
    </template>

    <template v-else-if="showForgotPasswordForm">
      <AccountForgotPasswordForm
        :is-loading="isPasswordResetLoading"
        :email="personalDetails.email"
        @submit="handleForgotPasswordSubmit"
        @back="handleBackFromForgotPassword"
        @email-change="handleEmailChange"
      />
    </template>

    <template v-else>
      <form class="form" @submit.prevent="submit()">
        <!--begin::Body-->
        <div class="card-body">
          <Heading :level="1" :title="t('general.signIn')" class="mb-6" />

          <Alert
            v-if="showPasswordResetAlert"
            :content="t('login.passwordResetSentAlertLabel')"
            class="mb-6"
            @close="hidePasswordResetAlert"
          />

          <div class="inputs">
            <div class="input">
              <label class="required form-label" for="email">
                {{ t('checkout.personalDetails.form.email') }}
              </label>
              <input
                id="email"
                v-model="personalDetails.email"
                autocomplete="email"
                class="form-control"
                data-test-id="email"
                name="email"
                :class="{
                  'is-invalid': invalidKeys.includes('email'),
                }"
                @input="clearErrors('email')"
              />
              <span
                v-if="invalidKeys.includes('email')"
                class="text-red text-xs"
              >
                {{ errors.email }}
              </span>
            </div>

            <div class="input">
              <label class="required form-label" for="password">
                {{ t('checkout.personalDetails.form.password') }}
              </label>
              <input
                id="password"
                v-model="personalDetails.password"
                autocomplete="current-password"
                class="form-control"
                data-test-id="password"
                name="password"
                type="password"
                :class="{
                  'is-invalid': invalidKeys.includes('password'),
                }"
                @input="clearErrors('password')"
              />

              <span
                v-if="invalidKeys.includes('password')"
                class="text-red text-xs"
              >
                {{ errors.password }}
              </span>
            </div>
          </div>

          <div class="forgot-pass-link">
            <button
              type="button"
              class="forgot-password-button"
              @click="handleForgotPassword"
            >
              {{ t('checkout.forgotPassword.title') }}
            </button>
          </div>

          <div class="d-flex flex-stack">
            <!--begin::Submit-->
            <Button color="primary" class="w-full mt-4">
              {{ t('general.signIn') }}
            </Button>
            <button class="btn btn-primary me-2 flex-shrink-0">
              <!--begin::Indicator label-->
              <span class="indicator-label"></span>
              <!--end::Indicator label-->
            </button>
            <!--end::Submit-->
          </div>
          <!--end::Actions-->
        </div>
        <!--begin::Body-->
      </form>
      <!--end::Form-->

      <div v-if="!isSubdomain" class="col-12 md:col-6 margin-auto">
        <SocialLogin />
      </div>
    </template>

    <div v-show="isFormInProgress" class="loader"></div>
  </div>
</template>

<script setup lang="ts">
import { toast, type ToastOptions } from 'vue3-toastify'
import { useFormValidation } from '~/composables/useFormValidation'
import { useIsSubdomain } from '~/composables/useSubdomain'
import { usePasswordReset } from '~/composables/usePasswordReset'
import AccountPasswordResetSent from '~/components/account/AccountPasswordResetSent.vue'
import AccountForgotPasswordForm from '~/components/account/AccountForgotPasswordForm.vue'

definePageMeta({
  middleware: ['already-auth'],
  layout: 'minimal',
})

const { t } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
const router = useRouter()

const isSubdomain = useIsSubdomain()
if (isSubdomain?.value) {
  throw createError({ statusCode: 404, statusMessage: 'Page Not Found' })
}

const { login } = useAuth()
const { error: passwordResetError, requestPasswordReset } = usePasswordReset()
const fullLocalizedUrl = useFullLocalizedUrl()

const isFormInProgress = ref<boolean>(false)
const personalDetails = ref<{
  email: string
  password: string
}>({
  email: '',
  password: '',
})
const rules = ref({ email: 'required|email', password: 'required|min:6' })
const { errors, clearErrors, validate, invalidKeys } = useFormValidation(
  personalDetails,
  rules
)

const showPasswordResetAlert = ref(
  route.query.password_reset_requested === 'true'
)
const hidePasswordResetAlert = () => {
  showPasswordResetAlert.value = false
}

const passwordResetSent = ref(false)
const showForgotPasswordForm = ref(false)
const resetEmail = ref('')
const isPasswordResetLoading = ref(false)

const handleForgotPassword = () => {
  showForgotPasswordForm.value = true
}

const handleForgotPasswordSubmit = async (data: { email: string }) => {
  isPasswordResetLoading.value = true

  await requestPasswordReset({
    email: data.email,
    reseturl: fullLocalizedUrl('/password-reset'),
  })

  if (passwordResetError.value && passwordResetError.value.length > 0) {
    toast(passwordResetError.value[0].message, {
      autoClose: 2000,
      type: 'error',
      position: toast.POSITION.TOP_CENTER,
      multiple: false,
    } as ToastOptions)
    isPasswordResetLoading.value = false
  } else {
    resetEmail.value = data.email
    passwordResetSent.value = true
    showForgotPasswordForm.value = false
    isPasswordResetLoading.value = false
  }
}

const handleBackFromForgotPassword = () => {
  showForgotPasswordForm.value = false
}

const handleBackFromPasswordReset = () => {
  passwordResetSent.value = false
  resetEmail.value = ''
}

const handleEmailChange = (email: string) => {
  personalDetails.value.email = email
}

const submit = async () => {
  if (isFormInProgress.value) return
  isFormInProgress.value = true

  await validate()

  if (invalidKeys.length > 0) {
    isFormInProgress.value = false
    return
  }

  try {
    await login(personalDetails.value.email, personalDetails.value.password)
    const redirectTo = route.query.redirectTo as string | undefined

    // If redirectTo doesn't start with a locale, use localePath
    // If it already starts with a locale, use it as is
    const targetPath = redirectTo
      ? redirectTo.startsWith('/de-de/') || redirectTo.startsWith('/en-us/')
        ? redirectTo
        : localePath(redirectTo)
      : localePath('/account/')

    router.push({ path: targetPath })
  } catch (e: any) {
    toast(t('general.error'), {
      autoClose: 3000,
      type: 'warning',
      position: toast.POSITION.TOP_CENTER,
      multiple: false,
      message: e?.message || t('general.error'),
    } as ToastOptions)
  } finally {
    isFormInProgress.value = false
  }
}
</script>

<style lang="postcss" scoped>
.auth-form {
  @apply max-w-lg w-full mx-auto py-20 relative;

  &.loading {
    @apply opacity-50 pointer-events-none;
  }
}

.inputs {
  @apply flex flex-col gap-4;
}

.input {
  @apply flex flex-col gap-2 flex-1;

  input {
    @apply w-full rounded-lg px-2 h-[37px] border border-gray-200;
  }
}

.forgot-pass-link {
  @apply mt-1 text-primary text-base;

  .forgot-password-button {
    @apply underline hover:no-underline border-0 p-0 cursor-pointer;
  }
}

.loader {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  position: absolute;
  animation: rotate 1s linear infinite;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  margin: auto;

  &::before {
    content: '';
    box-sizing: border-box;
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 5px solid #032e32;
    animation: prixClipFix 2s linear infinite;
  }
}

@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes prixClipFix {
  0% {
    clip-path: polygon(50% 50%, 0 0, 0 0, 0 0, 0 0, 0 0);
  }
  25% {
    clip-path: polygon(50% 50%, 0 0, 100% 0, 100% 0, 100% 0, 100% 0);
  }
  50% {
    clip-path: polygon(50% 50%, 0 0, 100% 0, 100% 100%, 100% 100%, 100% 100%);
  }
  75% {
    clip-path: polygon(50% 50%, 0 0, 100% 0, 100% 100%, 0 100%, 0 100%);
  }
  100% {
    clip-path: polygon(50% 50%, 0 0, 100% 0, 100% 100%, 0 100%, 0 0);
  }
}
</style>
